import { es } from "date-fns/locale"
import { toZonedTime, format as formatTz } from "date-fns-tz"

/**
 * Obtiene la zona horaria correspondiente al cliente.
 * @param client Nombre del cliente en minúsculas.
 * @returns Zona horaria como string (IANA).
 */
const getTimezoneByClient = (client: string): string => {
  switch (client.toLowerCase()) {
    case "entelperu":
      return "America/Lima"
    case "entelchile":
    case "wom":
    default:
      return "America/Santiago"
  }
}

/**
 * Formatea una fecha/hora según la zona horaria del cliente actual desde localStorage.
 * @param date Fecha en formato Date o string ISO.
 * @param formatStr Formato a aplicar. Por defecto "yyyy-MM-dd HH:mm".
 * @returns Fecha formateada o "--" si hay error.
 */
export const format_datetime_by_local_client = (
  date: string | Date,
  formatStr: string = "yyyy-MM-dd HH:mm"
): string => {
  try {
    const storedUser = localStorage.getItem("USER_SIMO")
    if (!storedUser) return "--"

    const parsed = JSON.parse(storedUser)
    
    const client =
      typeof parsed === "object" &&
      typeof parsed.client === "string"
        ? parsed.client
        : "entelchile"

    const timezone = getTimezoneByClient(client)

    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return "--"

    const zonedDate = toZonedTime(dateObj, timezone)
    return formatTz(zonedDate, formatStr, { timeZone: timezone, locale: es })
  } catch {
    return "--"
  }
}

/**
 * Formatea una fecha (sin hora) en zona horaria local del cliente.
 * @param date Fecha como string o Date.
 * @param formatString Formato deseado.
 * @returns Fecha formateada o "--".
 */
export const format_date = (date: Date | string, formatString: string = "yyyy-MM-dd") => {
  try {
    let dateTemp: Date
    dateTemp = new Date(date)
    return format_datetime_by_local_client(dateTemp, formatString)
  } catch (error) {
    return "--"
  }
}

/**
 * Formatea solo la hora según la zona horaria del cliente.
 * @param time Hora en formato Date o string (con o sin fecha).
 * @param formatString Formato de salida (por defecto HH:mm).
 * @returns Hora formateada.
 */
export const format_time = (time: Date | string, formatString: string = "HH:mm") => {
  try {
    let dateTemp: Date
    if (typeof time === "object") {
      dateTemp = time
    } else {
      if (time.includes("T")) {
        dateTemp = new Date(time)
      } else {
        dateTemp = new Date(`2000-01-01T${time}`)
      }
    }

    return format_datetime_by_local_client(dateTemp, formatString)
  } catch (error) {
    return "--"
  }
}

/**
 * Extrae hora manualmente como HH:mm desde un string.
 * @param time String en formato HH:mm:ss o con fecha ISO.
 * @returns Hora formateada como HH:mm.
 */
export const formatManualTime = (time: string) => {
  try {
    let dateTemp
    if (time.includes("T")) {
      dateTemp = time.split("T")
      dateTemp = dateTemp[1]
      dateTemp = dateTemp.split(":")
      return `${dateTemp[0]}:${dateTemp[1]}`
    } else {
      dateTemp = time.split(":")
      return `${dateTemp[0]}:${dateTemp[1]}`
    }
  } catch (error) {
    return "--"
  }
}

export const getTimezoneFromLocalStorage = (): string => {
  try {
    const storedUser = localStorage.getItem("USER_SIMO");
    if (!storedUser) return "America/Santiago";

    const parsed = JSON.parse(storedUser);
    
    const client =
      typeof parsed === "object" &&
      typeof parsed.client === "string"
        ? parsed.client
        : "entelchile";

    return getTimezoneByClient(client);
  } catch {
    return "America/Santiago";
  }
};